/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback } from 'react';
import { useLocation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

/**
 * Clears every applied refinement and returns to the first page of results.
 *
 * One implementation for the three controls that offer it: the overlay's `Clear Filters`
 * (`panel/overlay.tsx`), the applied-filter row's `Clear all` (`active-filters`), and the
 * over-filtered message's own button (`invalid-combination`). All three are reachable from the same
 * screen at the same time, so a second copy of this body is a promise that they will eventually
 * disagree — the next param that needs resetting, or the first analytics call on clear, would have
 * to land in three places or not at all.
 *
 * `preventScrollReset` is required, not a nicety. This is a real navigation, and
 * `<ScrollRestoration>` (root.tsx) scrolls to the top of every new location. A shopper who scrolled
 * the rail to reach the filter that emptied the page would otherwise be thrown to the top by the
 * button offering to undo it. It matters in the overlay too, which deliberately stays OPEN after a
 * clear: the reset would scroll the listing behind it, and the shopper would find themselves at the
 * top on dismissing it.
 */
export function useClearAllFilters(): () => void {
    const location = useLocation();
    const navigate = useNavigate();

    return useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete('refine');
        params.set('offset', '0');
        void navigate({ ...location, search: `?${params.toString()}` }, { preventScrollReset: true });
    }, [location, navigate]);
}
